import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { Box, Button, CircularProgress, IconButton, LinearProgress, TextField, Tooltip } from '@mui/material'
import { AttachFileRounded, CloseRounded, OpenInNewRounded } from '@mui/icons-material'
import {
    mentorReportApi,
    MrPsGroup,
    MrSession,
    PracticeMark,
    studentName,
} from '../../../features/curator/mentorReport'
import { HeadFiltersState } from '../../head/headData'
import { ui } from '../../../utils/uiTokens'
import { card, chip, ghostButton, Hint, inputSx, mentorNames, primaryButton, useGroupTitle } from './shared'

const MARKS: { value: PracticeMark; labelId: string }[] = [
    { value: 'WITH_CAMERA', labelId: 'withCamera' },
    { value: 'WITHOUT_CAMERA', labelId: 'withoutCamera' },
    { value: 'ABSENT', labelId: 'absent' },
]

const markSx = (active: boolean, value: PracticeMark) => {
    const t = value === 'ABSENT' ? 'bad' : value === 'WITH_CAMERA' ? 'ok' : 'info'
    return {
        ...(active ? chip(t) : {}),
        padding: '5px 10px',
        borderRadius: '24px',
        fontSize: '12.5px',
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
        userSelect: 'none' as const,
        border: active ? undefined : `1px solid ${ui.border}`,
        color: active ? undefined : ui.sub,
        whiteSpace: 'nowrap' as const,
    }
}

// Записи и отчёты ПС: по группе две карточки (ПС 1, ПС 2) - запись, посещение, итог.
// Правит только ментор группы (canEdit от бэкенда), басшы и директор видят то же только для чтения.
const PsReportView: React.FC<{ f: HeadFiltersState; mentorId?: number }> = ({ f, mentorId }) => {
    const month = f.monthNumber || 0
    const week = f.weekNumber || 0
    const groupTitle = useGroupTitle()
    const { data = [], isFetching } = mentorReportApi.useFetchPsWeekQuery(
        { courseId: f.courseId, groupId: f.groupId, monthNumber: month, weekNumber: week, mentorId },
        { skip: !month || !week },
    )

    if (!month || !week) return <Hint id="mrPickWeek" />

    return (
        <Box>
            {isFetching && <LinearProgress sx={{ marginBottom: '12px' }} />}
            {!isFetching && !data.length && <Hint id="mrNoGroups" />}
            {data.map((g) => (
                <Box key={`${g.group.groupId}:${g.group.courseSubjectId}`} sx={{ marginBottom: '24px' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '8px', marginBottom: '10px' }}>
                        <Box sx={{ fontWeight: 700, fontSize: '17px', color: ui.ink }}>{groupTitle(g.group)}</Box>
                        <Box sx={{ color: ui.muted, fontSize: '13px' }}>{mentorNames(g.group)}</Box>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
                        {g.sessions.map((s) => (
                            <SessionCard
                                key={`${s.sessionNumber}:${s.id ?? 0}:${s.updatedAt ?? ''}`}
                                group={g}
                                session={s}
                                month={month}
                                week={week}
                            />
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>
    )
}

interface CardProps {
    group: MrPsGroup
    session: MrSession
    month: number
    week: number
}

const SessionCard: React.FC<CardProps> = ({ group, session, month, week }) => {
    const { formatMessage } = useIntl()
    const editable = group.group.canEdit
    const field = session.sessionNumber === 1 ? 'practiceStatus' : 'practice2Status'
    const initialMarks = () =>
        Object.fromEntries(group.students.map((s) => [s.userSubjectEnrollId, (s[field] ?? '') as PracticeMark | '']))
    const [form, setForm] = useState({
        title: session.title ?? '',
        recordLink: session.recordLink ?? '',
        topic: session.topic ?? '',
        summary: session.summary ?? '',
        problems: session.problems ?? '',
    })
    const [marks, setMarks] = useState<Record<number, PracticeMark | ''>>(initialMarks)
    const [file, setFile] = useState<File | null>(null)
    const [removeFile, setRemoveFile] = useState(false)
    const fileInput = useRef<HTMLInputElement>(null)
    const [save, { isLoading }] = mentorReportApi.useSavePsMutation()

    // состав и отметки могли поменяться в «Практика және зачет»
    useEffect(() => setMarks(initialMarks()), [group.students])

    const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value })
    const toggle = (id: number, value: PracticeMark) => setMarks({ ...marks, [id]: marks[id] === value ? '' : value })
    const markAll = (value: PracticeMark) =>
        setMarks(Object.fromEntries(group.students.map((s) => [s.userSubjectEnrollId, value])))

    const changed = group.students.filter((s) => (s[field] ?? '') !== marks[s.userSubjectEnrollId])
    const came = group.students.filter((s) => marks[s.userSubjectEnrollId] === 'WITH_CAMERA' || marks[s.userSubjectEnrollId] === 'WITHOUT_CAMERA').length
    const fileUrl = removeFile ? null : session.fileUrl

    const handleSave = async () => {
        try {
            await save({
                groupId: group.group.groupId,
                courseSubjectId: group.group.courseSubjectId,
                monthNumber: month,
                weekNumber: week,
                sessionNumber: session.sessionNumber,
                ...form,
                removeFile,
                attendance: changed.map((s) => ({ userSubjectEnrollId: s.userSubjectEnrollId, status: marks[s.userSubjectEnrollId] ?? '' })),
                file,
            }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
        } catch (e) {}
    }

    const ro = !editable
    return (
        <Box sx={card}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                <Box sx={{ fontWeight: 700, fontSize: '15px', color: ui.ink }}>
                    {formatMessage({ id: 'mrSession' }, { n: session.sessionNumber })}
                </Box>
                <Box sx={chip(session.filled ? 'ok' : 'neutral')}>
                    {formatMessage({ id: session.filled ? 'mrFilled' : 'mrNotFilled' })}
                </Box>
                {session.updatedAt && (
                    <Box sx={{ color: ui.muted, fontSize: '12px', marginLeft: 'auto' }}>
                        {[session.mentorName, session.updatedAt.replace('T', ' ')].filter(Boolean).join(' · ')}
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'grid', gap: 1.5 }}>
                <TextField size="small" label={formatMessage({ id: 'mrRecordTitle' })} value={form.title} onChange={set('title')}
                    disabled={ro} inputProps={{ maxLength: 255 }} sx={inputSx} />
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField size="small" label={formatMessage({ id: 'mrRecordLink' })} placeholder="https://"
                        value={form.recordLink} onChange={set('recordLink')} disabled={ro} sx={inputSx} />
                    {session.recordLink && (
                        <Tooltip title={formatMessage({ id: 'mrOpen' })}>
                            <IconButton component="a" href={session.recordLink} target="_blank" rel="noopener noreferrer" sx={{ color: ui.pink }}>
                                <OpenInNewRounded fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    {fileUrl && (
                        <Box sx={{ ...chip('info'), maxWidth: '100%', overflow: 'hidden' }}>
                            <AttachFileRounded sx={{ fontSize: 15 }} />
                            <Box component="a" href={fileUrl} target="_blank" rel="noopener noreferrer"
                                sx={{ color: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {session.fileName || formatMessage({ id: 'mrFile' })}
                            </Box>
                            {editable && (
                                <CloseRounded sx={{ fontSize: 15, cursor: 'pointer' }} onClick={() => setRemoveFile(true)} />
                            )}
                        </Box>
                    )}
                    {file && (
                        <Box sx={{ ...chip('neutral'), maxWidth: '100%', overflow: 'hidden' }}>
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</Box>
                            <CloseRounded sx={{ fontSize: 15, cursor: 'pointer' }} onClick={() => setFile(null)} />
                        </Box>
                    )}
                    {editable && (
                        <>
                            <input ref={fileInput} type="file" hidden onChange={(e) => {
                                setFile(e.target.files?.[0] ?? null)
                                e.target.value = ''
                            }} />
                            <Button size="small" variant="outlined" startIcon={<AttachFileRounded />} sx={ghostButton}
                                onClick={() => fileInput.current?.click()}>
                                {formatMessage({ id: 'mrAttachFile' })}
                            </Button>
                            <Box sx={{ color: ui.muted, fontSize: '12px' }}>{formatMessage({ id: 'mrFileHint' })}</Box>
                        </>
                    )}
                </Box>
                <TextField size="small" label={formatMessage({ id: 'mrTopic' })} value={form.topic} onChange={set('topic')}
                    disabled={ro} inputProps={{ maxLength: 500 }} sx={inputSx} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, margin: '18px 0 8px' }}>
                <Box sx={{ fontWeight: 600, fontSize: '14px', color: ui.ink }}>
                    {formatMessage({ id: 'mrAttendance' }, { came, total: group.students.length })}
                </Box>
                {editable && group.students.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, marginLeft: 'auto', flexWrap: 'wrap' }}>
                        <Box sx={markSx(false, 'WITH_CAMERA')} onClick={() => markAll('WITH_CAMERA')}>
                            {formatMessage({ id: 'mrAllWithCamera' })}
                        </Box>
                    </Box>
                )}
            </Box>
            {!group.students.length && <Box sx={{ color: ui.muted, fontSize: '13px' }}>{formatMessage({ id: 'mrNoStudents' })}</Box>}
            <Box sx={{ border: group.students.length ? `1px solid ${ui.border}` : 'none', borderRadius: ui.radius, overflow: 'hidden' }}>
                {group.students.map((s, i) => (
                    <Box key={s.userSubjectEnrollId} sx={{
                        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, padding: '8px 10px',
                        borderTop: i ? `1px solid ${ui.border}` : 'none',
                    }}>
                        <Box sx={{ flex: '1 1 160px', minWidth: 0, fontSize: '14px', color: ui.ink }}>
                            {i + 1}. {studentName(s)}
                        </Box>
                        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {MARKS.map((m) => {
                                const active = marks[s.userSubjectEnrollId] === m.value
                                if (ro && !active) return null
                                return (
                                    <Box key={m.value} sx={{ ...markSx(active, m.value), cursor: ro ? 'default' : 'pointer' }}
                                        onClick={() => !ro && toggle(s.userSubjectEnrollId, m.value)}>
                                        {formatMessage({ id: m.labelId })}
                                    </Box>
                                )
                            })}
                            {ro && !marks[s.userSubjectEnrollId] && (
                                <Box sx={{ color: ui.muted, fontSize: '12.5px' }}>{formatMessage({ id: 'notMarked' })}</Box>
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>

            <Box sx={{ display: 'grid', gap: 1.5, marginTop: '16px' }}>
                <TextField size="small" multiline minRows={2} label={formatMessage({ id: 'mrSummary' })} value={form.summary}
                    onChange={set('summary')} disabled={ro} sx={inputSx} />
                <TextField size="small" multiline minRows={2} label={formatMessage({ id: 'mrProblems' })} value={form.problems}
                    onChange={set('problems')} disabled={ro} sx={inputSx} />
            </Box>

            {editable && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
                    <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={primaryButton}>
                        {isLoading ? <CircularProgress size={18} sx={{ color: ui.card }} /> : formatMessage({ id: 'save' })}
                    </Button>
                </Box>
            )}
        </Box>
    )
}

export default PsReportView
